import React, { useState } from "react";
import "./style.sass";
import useHandler from "./handler";
import { ClockTimePicker } from "./clock-time-picker";
import { CustomDatePicker } from "./custom-date-picker";

export const formatDurationDisplay = (val) => {
  if (val === null || val === undefined || val === "" || isNaN(Number(val))) {
    return "-";
  }
  const mins = Number(val);
  if (mins <= 0) return "-";
  if (mins < 60) {
    return `${mins} mins`;
  }
  const hrs = mins / 60;
  if (Number.isInteger(hrs)) {
    return `${hrs} ${hrs === 1 ? "hr" : "hrs"}`;
  }
  const rounded = Number(hrs.toFixed(2));
  return `${rounded} hrs`;
};

export const formatDateDisplay = (dateStr, timeStr = "") => {
  if (!dateStr && !timeStr) return "-";
  
  let formattedDate = "";
  if (dateStr) {
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const parts = dateStr.split("T")[0].split("-");
      if (parts.length === 3) {
        const year = parts[0];
        const monthIdx = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        if (monthNames[monthIdx]) {
          formattedDate = `${day} ${monthNames[monthIdx]} ${year}`;
        }
      }
    }
    if (!formattedDate) {
      formattedDate = dateStr;
    }
  }

  let formattedTime = "";
  if (timeStr) {
    if (/^\d{1,2}:\d{2}/.test(timeStr)) {
      const timeParts = timeStr.split(":");
      let hrs = parseInt(timeParts[0], 10);
      const mins = timeParts[1];
      const ampm = hrs >= 12 ? "PM" : "AM";
      hrs = hrs % 12 || 12;
      const hrsStr = hrs < 10 ? `${hrs}` : `${hrs}`;
      formattedTime = `${hrsStr}:${mins} ${ampm}`;
    } else {
      formattedTime = timeStr;
    }
  }

  if (formattedDate && formattedTime) {
    return `${formattedDate} ${formattedTime}`;
  }
  return formattedDate || formattedTime || "-";
};

export const TestCreation = () => {
  const [showClockModal, setShowClockModal] = useState(false);
  const {
    user,
    isSubmitting,
    viewMode,
    setViewMode,
    openDropdown,
    setOpenDropdown,
    dateFocused,
    setDateFocused,
    timeFocused,
    setTimeFocused,
    currentPage,
    setCurrentPage,
    totalTests,
    pageSize,
    existingTests,
    editingTestName,
    deleteModalTestName,
    setDeleteModalTestName,
    viewingTest,
    setViewingTest,
    formRef,
    formData,
    setFormData,
    categoryOptions,
    examOptions,
    classOptions,
    chapterOptions,
    subTopicOptions,
    toggleDropdown,
    handleChapterToggle,
    handleSubTopicToggle,
    handleResetForm,
    isFormValid,
    handleCreateTestSubmit,
    handleFetchSingleTest,
    handleDeleteTestConfirm,
    getInitials,
    tableFilters,
    handleTableFilterChange,
    handleClearTableFilters,
    hasActiveFilters,
    allTestNameOptions,
    allCategoryOptions,
    allExamOptions,
    allClassOptions,
    allChapterOptions,
    allSubTopicOptions,
    displayedTests,
    openFilterDropdown,
    setOpenFilterDropdown,
    filterBarRef,
  } = useHandler();

  const testNameSuggestions = tableFilters.testName.trim()
    ? allTestNameOptions.filter((name) =>
        name.toLowerCase().includes(tableFilters.testName.toLowerCase())
      )
    : [];

  return (
    <div className="test-creation-container">
      {/* Top Header */}
      <header className="test-creation-header">
        <div className="header-title-block">
          <h2>Test Creation</h2>
          <p>Create a test using existing tests or build a new one from scratch</p>
        </div>
      </header>

      {/* VIEW 1: Choose Mode Cards View */}
      {viewMode === "selection" && (
        <div className="selection-view-wrapper">
          <h3 className="test-creation-section-title">
            Choose how you want to create a test
          </h3>

          <div className="test-creation-grid">
            {/* Card 1: Create New Test */}
            <div className="test-creation-card">
              <div className="illustration-box purple-bg">
                <div className="preview-card">
                  <div className="preview-header-bar purple"></div>
                  <div className="preview-line-full"></div>
                  <div className="preview-item-row">
                    <div className="preview-dot"></div>
                    <div className="preview-line"></div>
                  </div>
                  <div className="preview-item-row">
                    <div className="preview-dot"></div>
                    <div className="preview-line"></div>
                  </div>
                  <div className="badge-circle purple">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <h4 className="card-title">Create New Test</h4>
                <p className="card-description">
                  Build a new test by selecting questions from the question bank based on your requirements.
                </p>
                <div className="card-divider"></div>
                <ul className="checklist">
                  <li>
                    <span className="check-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#12b76a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                      </svg>
                    </span>
                    Select questions from the question bank
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#12b76a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                      </svg>
                    </span>
                    Customize test settings and time
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#12b76a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                      </svg>
                    </span>
                    Organize by chapters, topics or concepts
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#12b76a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                      </svg>
                    </span>
                    Assign to students
                  </li>
                </ul>

                <button
                  className="action-button create-now-btn"
                  onClick={() => {
                    handleResetForm();
                    setViewMode("form");
                  }}
                >
                  Create Now
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <path d="M3.33334 8H12.6667M12.6667 8L8 3.33333M12.6667 8L8 12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Card 2: View Existing Test */}
            <div className="test-creation-card">
              <div className="illustration-box green-bg">
                <div className="preview-card">
                  <div className="preview-header-bar green"></div>
                  <div className="preview-line-full"></div>
                  <div className="preview-item-row">
                    <div className="preview-dot"></div>
                    <div className="preview-line"></div>
                  </div>
                  <div className="preview-item-row">
                    <div className="preview-dot"></div>
                    <div className="preview-line"></div>
                  </div>
                  <div className="badge-circle green">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <h4 className="card-title">View Existing Test</h4>
                <p className="card-description">
                  Select from your previously created tests and customize them as per your requirements.
                </p>
                <div className="card-divider"></div>
                <ul className="checklist">
                  <li>
                    <span className="check-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7f56d9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                      </svg>
                    </span>
                    Choose from all your existing tests
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7f56d9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                      </svg>
                    </span>
                    Edit test details, sections, and time
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7f56d9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                      </svg>
                    </span>
                    Add or remove questions
                  </li>
                  <li>
                    <span className="check-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7f56d9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9"></circle>
                        <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                      </svg>
                    </span>
                    Assign to students
                  </li>
                </ul>

                <button
                  className="action-button view-now-btn"
                  onClick={() => setViewMode("existing")}
                >
                  View Now
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <path d="M3.33334 8H12.6667M12.6667 8L8 3.33333M12.6667 8L8 12.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 2: Create / Edit Test Form Screen */}
      {viewMode === "form" && (
        <div className="create-test-form-wrapper" ref={formRef}>
          <div className="form-section-header">
            <button
              className="back-btn"
              onClick={() => {
                handleResetForm();
                setViewMode("selection");
              }}
              title="Back to selection"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
            <h3 className="form-title">
              {editingTestName ? "Edit Test" : "Create New Test"}
            </h3>
          </div>

          <form onSubmit={handleCreateTestSubmit}>
            <div className="form-grid">
              {/* Row 1 Left: Test Name */}
              <div className="form-group">
                <label>
                  Test Name <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter test name"
                  value={formData.testName}
                  onChange={(e) =>
                    setFormData({ ...formData, testName: e.target.value })
                  }
                />
              </div>

              {/* Row 1 Right: Test Category */}
              <div className="form-group dropdown-group">
                <label>
                  Test Category <span className="required-star">*</span>
                </label>
                <div
                  className={`custom-select-trigger ${openDropdown === "category" ? "active" : ""}`}
                  onClick={() => toggleDropdown("category")}
                >
                  <span className={formData.testCategory ? "selected-text" : "placeholder-text"}>
                    {formData.testCategory || "Select category"}
                  </span>
                  <svg className="chevron-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>

                {openDropdown === "category" && (
                  <div className="custom-dropdown-popover category-popover">
                    {categoryOptions.length > 0 ? (
                      categoryOptions.map((option) => (
                        <div
                          key={option}
                          className={`dropdown-item ${formData.testCategory === option ? "selected" : ""}`}
                          onClick={() => {
                            setFormData({ ...formData, testCategory: option });
                            setOpenDropdown(null);
                          }}
                        >
                          {option}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        No categories available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Row 2 Left: Exam */}
              <div className="form-group dropdown-group">
                <label>
                  Exam <span className="required-star">*</span>
                </label>
                <div
                  className={`custom-select-trigger ${openDropdown === "exam" ? "active" : ""}`}
                  onClick={() => toggleDropdown("exam")}
                >
                  <span className={formData.exam ? "selected-text" : "placeholder-text"}>
                    {formData.exam || "Select exam"}
                  </span>
                  <svg className="chevron-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>

                {openDropdown === "exam" && (
                  <div className="custom-dropdown-popover">
                    {examOptions.length > 0 ? (
                      examOptions.map((option) => (
                        <div
                          key={option}
                          className={`dropdown-item ${formData.exam === option ? "selected" : ""}`}
                          onClick={() => {
                            setFormData({
                              ...formData,
                              exam: option,
                              className: "",
                              chapters: [],
                              subTopic: [],
                            });
                            setOpenDropdown(null);
                          }}
                        >
                          {option}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        No exams available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Row 2 Right: Class */}
              <div className="form-group dropdown-group">
                <label>
                  Class <span className="required-star">*</span>
                </label>
                <div
                  className={`custom-select-trigger ${openDropdown === "class" ? "active" : ""}`}
                  onClick={() => toggleDropdown("class")}
                >
                  <span className={formData.className ? "selected-text" : "placeholder-text"}>
                    {formData.className || "Select class"}
                  </span>
                  <svg className="chevron-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>

                {openDropdown === "class" && (
                  <div className="custom-dropdown-popover">
                    {!formData.exam ? (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        Please select an Exam first
                      </div>
                    ) : classOptions.length > 0 ? (
                      classOptions.map((option) => (
                        <div
                          key={option}
                          className={`dropdown-item ${formData.className === option ? "selected" : ""}`}
                          onClick={() => {
                            setFormData({
                              ...formData,
                              className: option,
                              chapters: [],
                              subTopic: [],
                            });
                            setOpenDropdown(null);
                          }}
                        >
                          {option}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        No classes available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Row 3 Left: Chapter Multi-Select */}
              <div className="form-group dropdown-group">
                <label>
                  Chapter <span className="required-star">*</span>
                </label>
                <div
                  className={`custom-select-trigger ${openDropdown === "chapter" ? "active" : ""}`}
                  onClick={() => toggleDropdown("chapter")}
                >
                  <span className={formData.chapters.length > 0 ? "selected-text" : "placeholder-text"}>
                    {formData.chapters.length > 0
                      ? `${formData.chapters.length} Chapter${formData.chapters.length > 1 ? "s" : ""} Selected`
                      : "Select Chapter"}
                  </span>
                  <svg className="chevron-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>

                {openDropdown === "chapter" && (
                  <div className="custom-dropdown-popover chapter-popover">
                    {!formData.exam ? (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        Please select an Exam first
                      </div>
                    ) : !formData.className ? (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        Please select a Class first
                      </div>
                    ) : chapterOptions.length > 0 ? (
                      chapterOptions.map((chapter) => {
                        const isChecked = formData.chapters.includes(chapter);
                        return (
                          <div
                            key={chapter}
                            className="dropdown-item checkbox-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleChapterToggle(chapter);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}}
                              className="custom-checkbox"
                            />
                            <span>{chapter}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        No chapters available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Row 3 Right: Sub Topic Multi-Select */}
              <div className="form-group dropdown-group">
                <label>Sub Topic</label>
                <div
                  className={`custom-select-trigger ${openDropdown === "subtopic" ? "active" : ""}`}
                  onClick={() => toggleDropdown("subtopic")}
                >
                  <span
                    className={
                      Array.isArray(formData.subTopic) && formData.subTopic.length > 0
                        ? "selected-text"
                        : "placeholder-text"
                    }
                  >
                    {Array.isArray(formData.subTopic) && formData.subTopic.length > 0
                      ? `${formData.subTopic.length} Sub Topic${formData.subTopic.length > 1 ? "s" : ""} Selected`
                      : "Select topic"}
                  </span>
                  <svg className="chevron-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>

                {openDropdown === "subtopic" && (
                  <div className="custom-dropdown-popover subtopic-popover">
                    {formData.chapters.length === 0 ? (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        Please select a chapter first
                      </div>
                    ) : subTopicOptions.length === 0 ? (
                      <div className="dropdown-item disabled" style={{ color: "#667085", cursor: "default" }}>
                        No sub topics available
                      </div>
                    ) : (
                      subTopicOptions.map((option) => {
                        const isChecked =
                          Array.isArray(formData.subTopic) &&
                          formData.subTopic.includes(option);
                        return (
                          <div
                            key={option}
                            className="dropdown-item checkbox-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubTopicToggle(option);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}}
                              className="custom-checkbox"
                            />
                            <span>{option}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Row 4: Date, Time & Duration aligned in 3 columns */}
              <div className="date-time-duration-group">
                <div className="sub-group">
                  <label>
                    Date <span className="required-star">*</span>
                  </label>
                  <CustomDatePicker
                    value={formData.date}
                    onChange={(dateStr) =>
                      setFormData({ ...formData, date: dateStr })
                    }
                    placeholder="Select date"
                    dropUp={true}
                  />
                </div>

                <div className="sub-group">
                  <label>
                    Time <span className="required-star">*</span>
                  </label>
                  <div
                    className="input-with-icon"
                    onClick={() => setShowClockModal(true)}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="text"
                      readOnly
                      value={formData.displayTime || formData.time || ""}
                      placeholder="Select time"
                      style={{ cursor: "pointer" }}
                    />
                    <svg className="field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#612EF7" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                </div>

                <div className="sub-group duration-sub-group">
                  <label>
                    Duration <span className="required-star">*</span>
                  </label>
                  <div className="duration-input-wrapper">
                    <input
                      type="text"
                      placeholder="Enter duration (e.g. 15 or 1.5)"
                      value={formData.duration || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, duration: e.target.value })
                      }
                    />
                    <div className="duration-unit-trigger-wrapper">
                      <div
                        className={`duration-unit-trigger ${openDropdown === "durationUnit" ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown("durationUnit");
                        }}
                      >
                        <span>{formData.durationUnit === "hours" ? "Hours" : "Mins"}</span>
                        <svg className={`chevron ${openDropdown === "durationUnit" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>

                      {openDropdown === "durationUnit" && (
                        <div className="duration-unit-popover">
                          <div
                            className={`unit-item ${formData.durationUnit !== "hours" ? "selected" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormData((prev) => ({ ...prev, durationUnit: "mins" }));
                              toggleDropdown(null);
                            }}
                          >
                            Mins
                          </div>
                          <div
                            className={`unit-item ${formData.durationUnit === "hours" ? "selected" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormData((prev) => ({ ...prev, durationUnit: "hours" }));
                              toggleDropdown(null);
                            }}
                          >
                            Hours
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Form Action Buttons */}
            <div className="form-submit-container">
              <button
                type="button"
                className="reset-form-btn"
                onClick={handleResetForm}
              >
                Reset
              </button>
              <button
                type="submit"
                className="create-test-submit-btn"
                disabled={isSubmitting || !isFormValid}
              >
                {isSubmitting
                  ? editingTestName
                    ? "Updating..."
                    : "Creating..."
                  : editingTestName
                  ? "Update Test"
                  : "Create Test"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* VIEW 3: Existing Test Table View */}
      {viewMode === "existing" && (
        <div className="existing-test-view-wrapper">
          <div className="form-section-header">
            <button
              className="back-btn"
              onClick={() => {
                handleResetForm();
                setViewMode("selection");
              }}
              title="Back to selection"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
            <h3 className="form-title">Existing Test</h3>
          </div>

          {/* Existing Tests Filter Bar */}
          <div className="existing-tests-filter-card" ref={filterBarRef}>
            <div className="filter-card-header">
              <div className="filter-card-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                </svg>
                <span>Filter Tests</span>
              </div>
              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={handleClearTableFilters}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span>Reset</span>
                </button>
              )}
            </div>

            <div className="filters-grid">
              {/* 1. Test Name */}
              <div className="filter-item">
                <label>Test Name</label>
                <div className="test-name-input-wrapper">
                  <input
                    type="text"
                    placeholder="Search test name..."
                    value={tableFilters.testName}
                    onChange={(e) => {
                      handleTableFilterChange("testName", e.target.value);
                      setOpenFilterDropdown("testName");
                    }}
                    onFocus={() => setOpenFilterDropdown("testName")}
                  />
                </div>
                {openFilterDropdown === "testName" && tableFilters.testName.trim() !== "" && (
                  <div className="filter-dropdown-popover test-name-suggestions-popover">
                    {testNameSuggestions.length > 0 ? (
                      testNameSuggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="popover-item suggestion-item"
                          onClick={() => {
                            handleTableFilterChange("testName", suggestion);
                            setOpenFilterDropdown(null);
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6e3ff3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                          <span>{suggestion}</span>
                        </div>
                      ))
                    ) : (
                      <div className="popover-item no-suggestions">
                        No matching test names
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 2. Test Category */}
              <div className="filter-item">
                <label>Test Category</label>
                <div
                  className={`filter-select-trigger ${openFilterDropdown === "category" ? "active" : ""}`}
                  onClick={() => setOpenFilterDropdown(openFilterDropdown === "category" ? null : "category")}
                >
                  <span className="selected-val">{tableFilters.testCategory || "All Categories"}</span>
                  <svg className={`chevron ${openFilterDropdown === "category" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                {openFilterDropdown === "category" && (
                  <div className="filter-dropdown-popover">
                    <div
                      className={`popover-item ${tableFilters.testCategory === "" ? "selected" : ""}`}
                      onClick={() => {
                        handleTableFilterChange("testCategory", "");
                        setOpenFilterDropdown(null);
                      }}
                    >
                      All Categories
                    </div>
                    {allCategoryOptions.map((cat, idx) => (
                      <div
                        key={idx}
                        className={`popover-item ${tableFilters.testCategory === cat ? "selected" : ""}`}
                        onClick={() => {
                          handleTableFilterChange("testCategory", cat);
                          setOpenFilterDropdown(null);
                        }}
                      >
                        {cat}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 3. Exam */}
              <div className="filter-item">
                <label>Exam</label>
                <div
                  className={`filter-select-trigger ${openFilterDropdown === "exam" ? "active" : ""}`}
                  onClick={() => setOpenFilterDropdown(openFilterDropdown === "exam" ? null : "exam")}
                >
                  <span className="selected-val">{tableFilters.exam || "All Exams"}</span>
                  <svg className={`chevron ${openFilterDropdown === "exam" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                {openFilterDropdown === "exam" && (
                  <div className="filter-dropdown-popover">
                    <div
                      className={`popover-item ${tableFilters.exam === "" ? "selected" : ""}`}
                      onClick={() => {
                        handleTableFilterChange("exam", "");
                        setOpenFilterDropdown(null);
                      }}
                    >
                      All Exams
                    </div>
                    {allExamOptions.map((ex, idx) => (
                      <div
                        key={idx}
                        className={`popover-item ${tableFilters.exam === ex ? "selected" : ""}`}
                        onClick={() => {
                          handleTableFilterChange("exam", ex);
                          setOpenFilterDropdown(null);
                        }}
                      >
                        {ex}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 4. Class */}
              <div className="filter-item">
                <label>Class</label>
                <div
                  className={`filter-select-trigger ${openFilterDropdown === "class" ? "active" : ""}`}
                  onClick={() => setOpenFilterDropdown(openFilterDropdown === "class" ? null : "class")}
                >
                  <span className="selected-val">{tableFilters.className || "All Classes"}</span>
                  <svg className={`chevron ${openFilterDropdown === "class" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                {openFilterDropdown === "class" && (
                  <div className="filter-dropdown-popover">
                    <div
                      className={`popover-item ${tableFilters.className === "" ? "selected" : ""}`}
                      onClick={() => {
                        handleTableFilterChange("className", "");
                        setOpenFilterDropdown(null);
                      }}
                    >
                      All Classes
                    </div>
                    {!tableFilters.exam ? (
                      <div className="popover-item no-suggestions">Please select an Exam first</div>
                    ) : allClassOptions.length === 0 ? (
                      <div className="popover-item no-suggestions">No classes found for selected exam</div>
                    ) : (
                      allClassOptions.map((cls, idx) => (
                        <div
                          key={idx}
                          className={`popover-item ${tableFilters.className === cls ? "selected" : ""}`}
                          onClick={() => {
                            handleTableFilterChange("className", cls);
                            setOpenFilterDropdown(null);
                          }}
                        >
                          {cls}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* 5. Chapter */}
              <div className="filter-item">
                <label>Chapter</label>
                <div
                  className={`filter-select-trigger ${openFilterDropdown === "chapter" ? "active" : ""}`}
                  onClick={() => setOpenFilterDropdown(openFilterDropdown === "chapter" ? null : "chapter")}
                >
                  <span className="selected-val">{tableFilters.chapter || "All Chapters"}</span>
                  <svg className={`chevron ${openFilterDropdown === "chapter" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                {openFilterDropdown === "chapter" && (
                  <div className="filter-dropdown-popover">
                    <div
                      className={`popover-item ${tableFilters.chapter === "" ? "selected" : ""}`}
                      onClick={() => {
                        handleTableFilterChange("chapter", "");
                        setOpenFilterDropdown(null);
                      }}
                    >
                      All Chapters
                    </div>
                    {!tableFilters.className ? (
                      <div className="popover-item no-suggestions">Please select a Class first</div>
                    ) : allChapterOptions.length === 0 ? (
                      <div className="popover-item no-suggestions">No chapters found for selected class</div>
                    ) : (
                      allChapterOptions.map((ch, idx) => (
                        <div
                          key={idx}
                          className={`popover-item ${tableFilters.chapter === ch ? "selected" : ""}`}
                          onClick={() => {
                            handleTableFilterChange("chapter", ch);
                            setOpenFilterDropdown(null);
                          }}
                        >
                          {ch}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* 6. Subtopic */}
              <div className="filter-item">
                <label>Sub Topic</label>
                <div
                  className={`filter-select-trigger ${openFilterDropdown === "subTopic" ? "active" : ""}`}
                  onClick={() => setOpenFilterDropdown(openFilterDropdown === "subTopic" ? null : "subTopic")}
                >
                  <span className="selected-val">{tableFilters.subTopic || "All Sub Topics"}</span>
                  <svg className={`chevron ${openFilterDropdown === "subTopic" ? "open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                {openFilterDropdown === "subTopic" && (
                  <div className="filter-dropdown-popover">
                    <div
                      className={`popover-item ${tableFilters.subTopic === "" ? "selected" : ""}`}
                      onClick={() => {
                        handleTableFilterChange("subTopic", "");
                        setOpenFilterDropdown(null);
                      }}
                    >
                      All Sub Topics
                    </div>
                    {!tableFilters.chapter ? (
                      <div className="popover-item no-suggestions">Please select a Chapter first</div>
                    ) : allSubTopicOptions.length === 0 ? (
                      <div className="popover-item no-suggestions">No subtopics found for selected chapter</div>
                    ) : (
                      allSubTopicOptions.map((sub, idx) => (
                        <div
                          key={idx}
                          className={`popover-item ${tableFilters.subTopic === sub ? "selected" : ""}`}
                          onClick={() => {
                            handleTableFilterChange("subTopic", sub);
                            setOpenFilterDropdown(null);
                          }}
                        >
                          {sub}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>

          <div className="existing-table-card">
            <table className="existing-tests-table">
              <thead>
                <tr>
                  <th>Test Name</th>
                  <th>Subject</th>
                  <th>Exam Type</th>
                  <th>Class</th>
                  <th>Chapter</th>
                  <th>Sub Topic</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {displayedTests.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "36px", color: "#667085", fontSize: "14.5px" }}>
                      No tests found matching the selected filters.
                    </td>
                  </tr>
                ) : (
                  displayedTests.map((item, index) => (
                    <tr
                      key={index}
                      className="clickable-row"
                      onClick={() => handleFetchSingleTest(item.testName, "view")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="test-name-cell">{item.testName}</td>
                      <td>{item.subject}</td>
                      <td>{item.examType}</td>
                      <td>{item.className}</td>
                      <td>{item.chapter}</td>
                      <td>{item.subTopic}</td>
                      <td className="duration-cell">
                        {formatDurationDisplay(item.durationMinutes || item.duration_minutes)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer Pagination */}
          <div className="table-footer-pagination">
            <div className="showing-text">
              Showing {displayedTests.length} of {totalTests || existingTests.length}
            </div>

            <div className="pagination-controls">
              <button
                className="page-btn prev-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                &lt;
              </button>
              {Array.from(
                {
                  length: Math.max(
                    1,
                    Math.ceil((totalTests || existingTests.length || 1) / pageSize)
                  ),
                },
                (_, i) => i + 1
              ).map((p) => (
                <button
                  key={p}
                  className={`page-btn ${currentPage === p ? "active" : ""}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="page-btn next-btn"
                disabled={
                  currentPage >=
                  Math.ceil((totalTests || existingTests.length || 1) / pageSize)
                }
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Test Details Modal */}
      {viewingTest && (
        <div className="test-modal-overlay" onClick={() => setViewingTest(null)}>
          <div className="test-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="header-title-flex">
                <div className="icon-badge-circle">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6e3ff3" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                </div>
                <div>
                  <h3>Test Details</h3>
                  <p className="modal-subtitle">Comprehensive test overview</p>
                </div>
              </div>
              <button
                className="modal-close-btn"
                onClick={() => setViewingTest(null)}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="modal-hero-card">
              <div className="hero-test-name">
                {viewingTest.test_name || viewingTest.testName || "Assessment Test"}
              </div>
              <div className="hero-badges">
                <span className="badge-chip category-chip">
                  {viewingTest.test_category || viewingTest.testCategory || "Assessment"}
                </span>
                <span className="badge-chip exam-chip">
                  {viewingTest.exam || viewingTest.examType || "NEET"}
                </span>
              </div>
            </div>

            <div className="modal-details-grid">
              <div className="detail-item-box">
                <span className="box-label">Class</span>
                <span className="box-value">{viewingTest.class || viewingTest.className || "Class 12"}</span>
              </div>

              <div className="detail-item-box">
                <span className="box-label">Subject</span>
                <span className="box-value">{viewingTest.subject || "Physics"}</span>
              </div>

              <div className="detail-item-box full-width">
                <span className="box-label">Chapter</span>
                <span className="box-value">{viewingTest.chapter || "General Chapter"}</span>
              </div>

              <div className="detail-item-box full-width">
                <span className="box-label">Sub Topic</span>
                <span className="box-value">{viewingTest.sub_topic || viewingTest.subTopic || "General Topic"}</span>
              </div>

              <div className="detail-item-box full-width highlight-box">
                <span className="box-label">Scheduled Date, Time & Duration</span>
                <span className="box-value datetime-val">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6e3ff3" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  {viewingTest.test_date || viewingTest.date || "10 Nov 2026"} &nbsp;&nbsp;•&nbsp;&nbsp;
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6e3ff3" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  {viewingTest.test_time || viewingTest.time || "10:00 AM"} &nbsp;&nbsp;•&nbsp;&nbsp;
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6e3ff3" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 14 12 14 8"></polyline>
                  </svg>
                  {formatDurationDisplay(viewingTest.duration_minutes || viewingTest.durationMinutes)}
                </span>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-modal-btn"
                onClick={() => setViewingTest(null)}
              >
                Close
              </button>
              <button
                className="delete-test-modal-btn"
                style={{
                  background: "#fee4e2",
                  color: "#d92d20",
                  border: "1px solid #fda29b",
                  borderRadius: "12px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s ease"
                }}
                onClick={() => {
                  const tName = viewingTest.test_name || viewingTest.testName;
                  setViewingTest(null);
                  setDeleteModalTestName(tName);
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                Delete Test
              </button>
              <button
                className="edit-test-modal-btn"
                onClick={() => {
                  const tName = viewingTest.test_name || viewingTest.testName;
                  setViewingTest(null);
                  handleFetchSingleTest(tName, "edit");
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Edit Test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Test Confirmation Modal */}
      {deleteModalTestName && (
        <div className="test-modal-overlay" onClick={() => setDeleteModalTestName(null)}>
          <div className="test-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="header-title-flex">
                <div className="icon-badge-circle red">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d92d20" strokeWidth="2.2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </div>
                <div>
                  <h3>Delete Test</h3>
                  <p className="modal-subtitle">This action cannot be undone</p>
                </div>
              </div>
              <button
                className="modal-close-btn"
                onClick={() => setDeleteModalTestName(null)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body-delete">
              <p>Are you sure you want to permanently delete the test:</p>
              <div className="delete-test-name-box">"{deleteModalTestName}"</div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-modal-btn"
                onClick={() => setDeleteModalTestName(null)}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={handleDeleteTestConfirm}
              >
                Delete Test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Professional Clock Time Picker Modal */}
      <ClockTimePicker
        isOpen={showClockModal}
        initialTime={formData.displayTime || formData.time}
        onClose={() => setShowClockModal(false)}
        onSelectTime={(formatted24h, display12h) => {
          setFormData({
            ...formData,
            time: formatted24h,
            displayTime: display12h,
          });
        }}
      />
    </div>
  );
};

export default TestCreation;
